/**
 * /reset-password — Supabase password recovery completion.
 *
 * Recovery emails (initiated from `/forgot-password` via
 * `supabase.auth.resetPasswordForEmail`) land the user here with a
 * one-time `?code=...` query param (PKCE flow — the v2 default for our
 * `supabase` client; see `apps/web/src/auth/supabase.ts`).
 *
 * On mount we:
 *   1. Read `?code` from the URL.
 *   2. Call `supabase.auth.exchangeCodeForSession(code)` to establish a
 *      RECOVERY session for the recovering user. This overwrites any
 *      existing session.
 *   3. If exchange fails (code missing / expired / replayed) → render
 *      the `invalid` state with a link back to `/forgot-password`.
 *   4. If exchange succeeds → render the `form` state asking for a
 *      new password.
 *
 * On submit we:
 *   5. Validate the new password is ≥12 chars (matching the
 *      `AcceptInvitationPage` invariant).
 *   6. Call `supabase.auth.updateUser({ password })`.
 *   7. On success → render the transient `success` state and navigate
 *      to `/login` (the user must re-authenticate with the new
 *      password — the recovery session is one-shot).
 *
 * SECURITY POSTURE:
 *   - The new password is NEVER logged, NEVER persisted outside form
 *     state, NEVER serialised into any thrown error or state field.
 *   - The recovery `code` from the URL is consumed exactly once by
 *     `exchangeCodeForSession`. A second mount with the same code
 *     hits the `invalid` branch.
 *   - On success we explicitly `signOut()` BEFORE navigating to /login
 *     so the recovery session can't be silently reused. The user must
 *     log in fresh with the new password.
 *   - The exchange error is collapsed to a single Arabic "expired"
 *     message — we do NOT surface Supabase's raw error text (which
 *     varies across versions and could leak implementation detail).
 *
 * Operator note: `/reset-password` MUST be added to the Supabase
 * Dashboard's `Authentication → URL Configuration → Redirect URLs`
 * whitelist. Without that, the link in the recovery email substitutes
 * the Site URL and the user never sees this page. See
 * `apps/web/.env.example` for the operator checklist.
 *
 * Visual: same `<AuthShell>` split-screen as the sibling auth pages.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../auth/supabase';
import { env } from '../../env';
import { AuthShell } from '../../components/auth/AuthShell';

type Stage = 'verifying' | 'form' | 'submitting' | 'success' | 'invalid';

/**
 * Minimum new-password length. Mirrors the gate in `AcceptInvitationPage`
 * — if you change one, change both.
 */
const PASSWORD_MIN_LEN = 12;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = params.get('code') ?? '';

  const [stage, setStage] = useState<Stage>(code.length === 0 ? 'invalid' : 'verifying');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guard against the React 18 strict-mode double-mount consuming
  // the recovery code twice (which would always 'invalid' the
  // second pass because the code is one-shot).
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    if (code.length === 0) {
      // No code → invalid set in initial state; nothing to do.
      return;
    }
    if (exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    if (!env.isConfigured) {
      setError('متغيرات البيئة Supabase غير مهيّأة. راجع ملف apps/web/.env.example.');
      setStage('invalid');
      return;
    }

    // Codex P2 — cancellation flag so a pending exchange doesn't call
    // state setters after the component unmounts, and doesn't leave
    // the user-visible stage flipped to 'form' if they navigated away
    // mid-flow. (The session-side effect of exchangeCodeForSession
    // still happens — Supabase's client owns it — but the UI state
    // is gated by this flag.)
    let cancelled = false;

    (async () => {
      try {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (cancelled) return;
        if (exchangeError) {
          setStage('invalid');
          return;
        }
        setStage('form');
      } catch {
        if (cancelled) return;
        setStage('invalid');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < PASSWORD_MIN_LEN) {
      setError(`كلمة المرور يجب أن تكون ${PASSWORD_MIN_LEN} خانة على الأقل.`);
      return;
    }
    setStage('submitting');
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        // Generic copy — we don't show Supabase's raw error message.
        setError('تعذّر تحديث كلمة المرور. حاول مجدداً أو اطلب رابطاً جديداً.');
        setStage('form');
        return;
      }

      // Codex P1-2 — burn the recovery session ATOMICALLY before
      // navigating. If signOut + getSession-verify can't confirm the
      // session is gone, do NOT navigate to /login (LoginPage
      // auto-redirects authenticated users into /app, which would
      // bypass the intended re-authentication gate and leave the
      // user signed in via the just-burnt recovery code).
      //
      // `scope: 'global'` invalidates the session on the auth server
      // (not just localStorage) so a parallel session on another
      // device — e.g. one created by an attacker who also held the
      // recovery link — is also burnt.
      const { error: signOutError } = await supabase.auth.signOut({ scope: 'global' });
      // Verify the session is actually cleared client-side regardless
      // of what signOut reported. supabase.auth.getSession() reads
      // from localStorage + applies refresh logic; null means the
      // browser has no session and LoginPage will render the form
      // (not auto-redirect).
      const { data: postSession } = await supabase.auth.getSession();
      if (signOutError || postSession?.session !== null) {
        // Hard fail: password was updated but we couldn't guarantee
        // the recovery session is gone. Render a blocking warning
        // rather than silently navigating and risk auto-login.
        setError(
          'تم تحديث كلمة المرور، لكن تعذّر إنهاء الجلسات تلقائياً. ' +
            'سجّل الخروج يدوياً من قائمة المستخدم على هذا الجهاز وعلى الأجهزة الأخرى، ' +
            'ثم سجّل دخولك بكلمة المرور الجديدة.',
        );
        setStage('form');
        return;
      }

      setStage('success');
      // Brief pause so the success state is visible before navigation.
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch {
      setError('حدث خطأ غير متوقع. حاول مجدداً.');
      setStage('form');
    }
  }

  if (stage === 'invalid') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <div className="auth-state-glyph auth-state-glyph--error">
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
          </div>
          <div className="auth-eyebrow" style={{ textAlign: 'center' }}>
            رابط غير صالح
          </div>
          <h1 className="auth-title" style={{ textAlign: 'center' }}>
            رابط إعادة <em>التعيين منتهي</em>
          </h1>
          <p className="auth-sub" style={{ textAlign: 'center', marginBottom: 24 }}>
            الرابط ناقص أو منتهي الصلاحية أو سبق استخدامه. اطلب رابطاً جديداً من صفحة "نسيت كلمة
            المرور؟".
          </p>
          <Link to="/forgot-password" className="auth-cta" style={{ textDecoration: 'none' }}>
            <span>طلب رابط جديد</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
          <p className="tiny" style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 22 }}>
            <Link to="/login" style={{ color: 'inherit' }}>
              العودة إلى تسجيل الدخول
            </Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  if (stage === 'verifying') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <div className="auth-eyebrow" style={{ textAlign: 'center' }}>
            جاري التحقق
          </div>
          <h1 className="auth-title" style={{ textAlign: 'center' }}>
            تحقق من <em>الرابط</em>
          </h1>
          <p className="auth-sub" style={{ textAlign: 'center', marginBottom: 24 }}>
            <span className="spinner" aria-hidden="true" /> لحظات…
          </p>
        </div>
      </AuthShell>
    );
  }

  if (stage === 'success') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <div className="auth-eyebrow" style={{ textAlign: 'center' }}>
            تم التحديث
          </div>
          <h1 className="auth-title" style={{ textAlign: 'center' }}>
            كلمة المرور <em>محدّثة</em>
          </h1>
          <p className="auth-sub" style={{ textAlign: 'center', marginBottom: 24 }}>
            جاري التوجيه إلى صفحة تسجيل الدخول…
          </p>
        </div>
      </AuthShell>
    );
  }

  const submitting = stage === 'submitting';

  return (
    <AuthShell>
      <div className="auth-eyebrow">كلمة مرور جديدة</div>
      <h1 className="auth-title">
        اختر <em>كلمة مرور قوية</em>
      </h1>
      <p className="auth-sub">
        اختر كلمة مرور لا تقل عن {PASSWORD_MIN_LEN} خانة. لا تستخدم كلمة مرور سبق استخدامها في موقع
        آخر.
      </p>
      <p className="tiny" style={{ color: 'var(--muted)', marginTop: -8, marginBottom: 16 }}>
        ملاحظة: حفظ كلمة المرور الجديدة سيُسجّل خروجك من جميع الأجهزة الأخرى. ستحتاج لإعادة تسجيل
        الدخول.
      </p>

      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <div className="banner banner-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        ) : null}

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="password">
            كلمة المرور الجديدة
          </label>
          <div className="auth-input-wrap">
            <input
              id="password"
              className="auth-input auth-input--has-eye"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              placeholder="••••••••••••"
              disabled={submitting}
              autoComplete="new-password"
              dir="ltr"
            />
            <svg
              className="auth-input-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="4" y="10" width="16" height="11" rx="2" />
              <path d="M8 10V7a4 4 0 1 1 8 0v3" />
            </svg>
            <button
              type="button"
              className="auth-input-eye"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              aria-pressed={showPassword}
              disabled={submitting}
            >
              {showPassword ? (
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M3 3l18 18" />
                  <path d="M10.6 6.1A11.3 11.3 0 0 1 12 6c6 0 10 6 10 6a14.4 14.4 0 0 1-3 3.6" />
                  <path d="M6.7 6.7A14.5 14.5 0 0 0 2 12s4 6 10 6c1.6 0 3-.4 4.3-1" />
                  <circle cx="12" cy="12" r="2.5" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <button type="submit" className="auth-cta" disabled={submitting} style={{ marginTop: 8 }}>
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري الحفظ…' : 'حفظ كلمة المرور'}</span>
          {!submitting ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          ) : null}
        </button>

        <p className="tiny" style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 22 }}>
          <Link to="/login" style={{ color: 'inherit' }}>
            العودة إلى تسجيل الدخول
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
